// =============================================================================
// AGENT 3 — INŻYNIER LOGIKI: SILNIK SCORINGU
// Funkcje deterministyczne. Zero importów CSS lub JSX.
// =============================================================================

import type { Worker, Task, Assignment, AssignmentVerdict, ScoreResult } from '../types/models';

// ---------------------------------------------------------------------------
// Wagi punktowe
// ---------------------------------------------------------------------------

const POINTS = {
  SKILL_MATCH: 40,
  SKILL_MISMATCH_PENALTY: -30,
  PRIORITY_BONUS: { awaria: 20, pilne: 10, normalne: 0, rutynowe: 0 },
  OVERLOAD_PENALTY: -15, // workload > 70
  EXCESS_TASK_PENALTY: -10, // per task above 5 per worker
  IGNORED_AWARIA_PENALTY: -8, // per non-awaria assignment while awaria was visible
} as const;

// ---------------------------------------------------------------------------
// Obliczanie optymalnego przydziału (silnik AI)
// ---------------------------------------------------------------------------

export function computeAiAssignments(
  tasks: Task[],
  workers: Worker[],
): Assignment[] {
  return tasks
    .slice()
    .sort((a, b) => {
      const priorityOrder = { awaria: 0, pilne: 1, normalne: 2, rutynowe: 3 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    })
    .map((task) => {
      const ranked = workers
        .filter((w) => w.available)
        .map((w) => ({ worker: w, score: scoreWorkerForTask(w, task) }))
        .sort((a, b) => b.score - a.score);

      const best = ranked[0]?.worker ?? workers[0];

      return { taskId: task.id, workerId: best.id, assignedAt: 0 };
    });
}

// ---------------------------------------------------------------------------
// Obliczanie wyniku gracza
// ---------------------------------------------------------------------------

export function calculateScore(
  playerAssignments: Assignment[],
  tasks: Task[],
  workers: Worker[],
  timeSpentSeconds: number,
  decisionMeta?: {
    ignoredAwariaByTaskId?: Record<string, boolean>;
    excessTaskByTaskId?: Record<string, boolean>;
  },
): ScoreResult {
  const aiAssignments = computeAiAssignments(tasks, workers);
  const ignoredAwariaByTaskId = decisionMeta?.ignoredAwariaByTaskId ?? {};
  const excessTaskByTaskId = decisionMeta?.excessTaskByTaskId ?? {};

  const verdicts: AssignmentVerdict[] = tasks.map((task) => {
    const playerChoice = playerAssignments.find((a) => a.taskId === task.id);
    const aiChoice = aiAssignments.find((a) => a.taskId === task.id)!;
    const ignoredVisibleAwaria = !!ignoredAwariaByTaskId[task.id];
    const exceededWorkerTaskLimit = !!excessTaskByTaskId[task.id];
    const stepPenalty =
      (ignoredVisibleAwaria ? POINTS.IGNORED_AWARIA_PENALTY : 0) +
      (exceededWorkerTaskLimit ? POINTS.EXCESS_TASK_PENALTY : 0);

    if (!playerChoice) {
      const aiWorker = workers.find((w) => w.id === aiChoice.workerId)!;

      return {
        taskId: task.id,
        workerId: '',
        playerChoice: { taskId: task.id, workerId: '', assignedAt: 0 },
        aiChoice,
        pointsEarned: 0,
        maxPoints: maxPointsForTask(task, workers),
        decisionLevel: 3,
        penaltyFlags: {
          ignoredVisibleAwaria,
          exceededWorkerTaskLimit,
        },
        explanation: buildExplanation(task, null, aiWorker, 3, {
          ignoredVisibleAwaria,
          exceededWorkerTaskLimit,
        }),
      };
    }

    const playerWorker = workers.find((w) => w.id === playerChoice.workerId)!;
    const aiWorker = workers.find((w) => w.id === aiChoice.workerId)!;
    const earned = scoreWorkerForTask(playerWorker, task);
    const scoredWithStepPenalty = earned + stepPenalty;
    const max = maxPointsForTask(task, workers);
    const hasRequiredCompetence = playerWorker.skills.includes(task.requiredSkill)
      || playerWorker.skills.includes('uniwersalny');
    const decisionLevel: 1 | 2 | 3 = !hasRequiredCompetence
      ? 3
      : (ignoredVisibleAwaria || exceededWorkerTaskLimit) ? 2 : 1;

    return {
      taskId: task.id,
      workerId: playerChoice.workerId,
      playerChoice,
      aiChoice,
      pointsEarned: Math.max(0, scoredWithStepPenalty),
      maxPoints: max,
      decisionLevel,
      penaltyFlags: {
        ignoredVisibleAwaria,
        exceededWorkerTaskLimit,
      },
      explanation: buildExplanation(task, playerWorker, aiWorker, decisionLevel, {
        ignoredVisibleAwaria,
        exceededWorkerTaskLimit,
      }),
    };
  });

  const totalPoints = verdicts.reduce((sum, v) => sum + v.pointsEarned, 0);
  const maxPossiblePoints = verdicts.reduce((sum, v) => sum + v.maxPoints, 0);

  return {
    totalPoints,
    maxPossiblePoints,
    matchPercentage: maxPossiblePoints > 0
      ? Math.round((totalPoints / maxPossiblePoints) * 100)
      : 0,
    timeSpentSeconds,
    verdicts,
  };
}

// ---------------------------------------------------------------------------
// Funkcje pomocnicze (czyste)
// ---------------------------------------------------------------------------

function scoreWorkerForTask(worker: Worker, task: Task): number {
  let points = 0;

  if (worker.skills.includes(task.requiredSkill)) {
    points += POINTS.SKILL_MATCH;
  } else {
    points += POINTS.SKILL_MISMATCH_PENALTY;
  }

  points += POINTS.PRIORITY_BONUS[task.priority];

  if (worker.workload > 70) {
    points += POINTS.OVERLOAD_PENALTY;
  }

  return points;
}

function maxPointsForTask(task: Task, workers: Worker[]): number {
  return Math.max(...workers.map((w) => Math.max(0, scoreWorkerForTask(w, task))));
}

function buildExplanation(
  task: Task,
  playerWorker: Worker | null,
  _aiWorker: Worker,
  decisionLevel: 1 | 2 | 3,
  penaltyFlags: {
    ignoredVisibleAwaria: boolean;
    exceededWorkerTaskLimit: boolean;
  },
): string {
  if (!playerWorker) {
    return 'To zgłoszenie nie zostało przypisane do żadnego pracownika, więc decyzja jest niepoprawna.';
  }

  if (decisionLevel === 1) {
    return `Poprawnie: ${playerWorker.name} ma wymaganą kompetencję "${task.requiredSkill}" i w tym kroku nie naliczono kar kontekstowych.`;
  }

  if (decisionLevel === 2) {
    const penalties: string[] = [];

    if (penaltyFlags.ignoredVisibleAwaria) {
      penalties.push('w momencie decyzji była widoczna awaria, a przypisano inne zgłoszenie');
    }

    if (penaltyFlags.exceededWorkerTaskLimit) {
      penalties.push(`przekroczono limit 5 zadań dla pracownika ${playerWorker.name}`);
    }

    return `Dobre dopasowanie do ${playerWorker.name}, ale naliczono karę: ${penalties.join(' oraz ')}.`;
  }

  if (!playerWorker.skills.includes(task.requiredSkill) && !playerWorker.skills.includes('uniwersalny')) {
    return `Niepoprawnie: ${playerWorker.name} nie ma wymaganej kompetencji "${task.requiredSkill}" dla tego zgłoszenia.`;
  }

  return `Niepoprawnie: przypisanie do ${playerWorker.name} nie spełnia warunków poprawnego dopasowania dla tego zgłoszenia.`;
}
